import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../shared/theme';

// Mirror of BADGE_DEFS from ProfileBadges — kept local to avoid circular deps
const BADGE_DISPLAY = {
  game: {
    bronze: { icon: '🎮', label: 'Gamer',          labelNL: 'Speler',          color: '#8B7355' },
    silver: { icon: '🎮', label: 'Regular Gamer',   labelNL: 'Vaste Speler',    color: '#7B6FD0' },
    gold:   { icon: '🏆', label: 'Game Master',     labelNL: 'Game Master',     color: '#C8A84B' },
  },
  exercise: {
    bronze: { icon: '💪', label: 'Beginner',        labelNL: 'Beginner',        color: '#8B7355' },
    silver: { icon: '💪', label: 'Active',          labelNL: 'Actief',          color: '#4AC87A' },
    gold:   { icon: '🔥', label: 'Athlete',         labelNL: 'Atleet',          color: '#D4873A' },
  },
  streak: {
    bronze: { icon: '🔥', label: '3-Day Streak',    labelNL: '3-Daagse Reeks',  color: '#D4873A' },
    silver: { icon: '🔥', label: 'Week Warrior',    labelNL: 'Week Strijder',   color: '#5B9ED0' },
    gold:   { icon: '⚡', label: 'Month Champion',  labelNL: 'Maand Kampioen',  color: '#C8A84B' },
  },
  stability: {
    silver: { icon: '⚖️', label: 'Balanced',        labelNL: 'Evenwichtig',     color: '#5B9ED0' },
    gold:   { icon: '🏛️', label: 'Stable Person',   labelNL: 'Stabiel Persoon', color: '#C8A84B' },
  },
  social: {
    silver: { icon: '🌐', label: 'Social',          labelNL: 'Sociaal',         color: '#2A9E82' },
    gold:   { icon: '💫', label: 'Very Social',     labelNL: 'Zeer Sociaal',    color: '#7B6FD0' },
  },
  entrepreneur: {
    gold:   { icon: '🏢', label: 'Entrepreneur',    labelNL: 'Ondernemer',      color: '#C8A84B' },
  },
  coach: {
    gold:   { icon: '🎓', label: 'Coach',           labelNL: 'Coach',           color: '#7B6FD0' },
  },
};

const LEVEL_LABEL = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };
const LEVEL_LABEL_NL = { bronze: 'Brons', silver: 'Zilver', gold: 'Goud' };

export default function BadgeUnlockModal({ badge, lang, onClose }) {
  const nl = lang === 'nl';
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState([]);

  const def = badge ? (BADGE_DISPLAY[badge.type]?.[badge.level] || null) : null;

  useEffect(() => {
    if (!badge || !def) return;
    // Slight delay so the pop-in animation fires after mount
    const t = setTimeout(() => setVisible(true), 30);
    // Generate sparkle particles
    setParticles(Array.from({ length: 12 }, (_, i) => ({
      id: i,
      angle: (i / 12) * 360,
      dist: 60 + Math.random() * 40,
      delay: Math.random() * 0.4,
      size: 4 + Math.random() * 6,
    })));
    // Auto-close after 4s
    const autoClose = setTimeout(() => handleClose(), 4500);
    return () => { clearTimeout(t); clearTimeout(autoClose); };
  }, [badge]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  if (!badge || !def) return null;

  const levelColor = def.color;

  return ReactDOM.createPortal(
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        transition: 'opacity 0.3s',
        opacity: visible ? 1 : 0,
      }}
    >
      <style>{`
        @keyframes badgePop {
          0%   { transform: scale(0.4) rotate(-10deg); opacity: 0; }
          60%  { transform: scale(1.12) rotate(3deg); opacity: 1; }
          80%  { transform: scale(0.96) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes particleFly {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
        }
        @keyframes shimmer {
          0%,100% { opacity: 0.6; }
          50%      { opacity: 1; }
        }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: `linear-gradient(160deg, #0d1625 0%, #111e30 100%)`,
          border: `2px solid ${levelColor}55`,
          borderRadius: 28,
          padding: '40px 32px 32px',
          maxWidth: 320, width: '100%',
          textAlign: 'center',
          boxShadow: `0 0 60px ${levelColor}33, 0 24px 60px rgba(0,0,0,0.7)`,
          position: 'relative',
          overflow: 'hidden',
          animation: visible ? 'badgePop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards' : 'none',
        }}
      >
        {/* Close button */}
        <button onClick={handleClose} style={{position:'absolute',top:12,right:12,background:'transparent',border:'none',color:'rgba(255,255,255,0.4)',fontSize:20,cursor:'pointer',padding:4,lineHeight:1,zIndex:2}}>✕</button>
        {/* Glow background */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 180, height: 180, borderRadius: '50%',
          background: `radial-gradient(circle, ${levelColor}22 0%, transparent 70%)`,
          pointerEvents: 'none',
          animation: 'shimmer 2s ease-in-out infinite',
        }}/>

        {/* Particles */}
        {visible && particles.map(p => {
          const rad = (p.angle * Math.PI) / 180;
          const tx = Math.cos(rad) * p.dist;
          const ty = Math.sin(rad) * p.dist;
          return (
            <div key={p.id} style={{
              position: 'absolute', top: '38%', left: '50%',
              width: p.size, height: p.size, borderRadius: '50%',
              background: levelColor,
              '--tx': `${tx}px`, '--ty': `${ty}px`,
              animation: `particleFly 0.8s ${p.delay}s ease-out forwards`,
              opacity: 0,
              pointerEvents: 'none',
            }}/>
          );
        })}

        {/* Level label */}
        <div style={{
          fontSize: 10, letterSpacing: 3, fontWeight: 700,
          color: levelColor, marginBottom: 20,
        }}>
          {nl ? `${LEVEL_LABEL_NL[badge.level]} BADGE VRIJGESCHAKELD` : `${LEVEL_LABEL[badge.level]} BADGE UNLOCKED`}
        </div>

        {/* Badge icon */}
        <div style={{
          fontSize: 72, lineHeight: 1, marginBottom: 16,
          filter: `drop-shadow(0 0 20px ${levelColor}88)`,
        }}>
          {def.icon}
        </div>

        {/* Badge name */}
        <div style={{
          fontSize: 22, fontWeight: 800,
          color: C.white, marginBottom: 8,
          fontFamily: 'Georgia, serif',
        }}>
          {nl ? def.labelNL : def.label}
        </div>

        {/* Level pill */}
        <div style={{
          display: 'inline-block',
          background: `${levelColor}22`,
          border: `1px solid ${levelColor}55`,
          borderRadius: 20, padding: '4px 14px',
          fontSize: 12, fontWeight: 700, color: levelColor,
          marginBottom: 24,
        }}>
          {nl ? LEVEL_LABEL_NL[badge.level] : LEVEL_LABEL[badge.level]}
        </div>

        {/* Dismiss */}
        <button
          onClick={handleClose}
          style={{
            display: 'block', width: '100%',
            background: `linear-gradient(135deg, ${levelColor}, ${levelColor}bb)`,
            border: 'none', borderRadius: 14,
            color: '#0a1220', fontSize: 14, fontWeight: 700,
            padding: '13px 0', cursor: 'pointer',
          }}
        >
          {nl ? '🎉 Geweldig!' : '🎉 Awesome!'}
        </button>
      </div>
    </div>,
    document.body
  );
}
